import { useCallback, useEffect, useMemo, useState } from "react";

export const COMPANY_FORMS_UPLOAD_TABLE_COLUMNS_KEY =
  "cms_company_forms_upload_table_columns";
export const COMPANY_FORMS_APPROVE_TABLE_COLUMNS_KEY =
  "cms_company_forms_approve_table_columns";

export const COMPANY_FORMS_TABLE_COLUMNS = [
  { key: "select", title: "", hideable: false, defaultVisible: true },
  { key: "document", title: "Document", hideable: false, defaultVisible: true },
  { key: "destination", title: "Destination", defaultVisible: true },
  { key: "category", title: "Category", defaultVisible: true },
  {
    key: "uploader",
    title: "Uploaded by",
    defaultVisible: false,
  },
  { key: "status", title: "Status", hideable: false, defaultVisible: true },
  { key: "rejection_note", title: "Rejection note", defaultVisible: true },
  { key: "actions", title: "Action", hideable: false, defaultVisible: true },
];

function buildDefaultVisibility(columns) {
  return columns.reduce((acc, column) => {
    acc[column.key] =
      column.hideable === false ? true : column.defaultVisible !== false;
    return acc;
  }, {});
}

function loadVisibility(storageKey, columns) {
  const defaults = buildDefaultVisibility(columns);
  try {
    const saved = localStorage.getItem(storageKey);
    if (!saved) return defaults;
    const parsed = JSON.parse(saved);
    return { ...defaults, ...parsed };
  } catch {
    return defaults;
  }
}

export function useCompanyFormsTableColumns(storageKey) {
  const columns = COMPANY_FORMS_TABLE_COLUMNS;
  const [visibility, setVisibility] = useState(() =>
    loadVisibility(storageKey, columns),
  );
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    setVisibility(loadVisibility(storageKey, columns));
  }, [storageKey, columns]);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(visibility));
  }, [storageKey, visibility]);

  const visibleColumns = useMemo(
    () => columns.filter((column) => visibility[column.key] !== false),
    [columns, visibility],
  );

  const hideableColumns = useMemo(
    () => columns.filter((column) => column.hideable !== false),
    [columns],
  );

  const toggleColumn = useCallback(
    (key) => {
      const column = columns.find((item) => item.key === key);
      if (!column || column.hideable === false) return;
      setVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
    },
    [columns],
  );

  const resetToDefaults = useCallback(() => {
    setVisibility(buildDefaultVisibility(columns));
  }, [columns]);

  const isColumnVisible = useCallback(
    (key) => visibleColumns.some((col) => col.key === key),
    [visibleColumns],
  );

  return {
    visibility,
    visibleColumns,
    hideableColumns,
    toggleColumn,
    resetToDefaults,
    isColumnVisible,
    settingsOpen,
    setSettingsOpen,
  };
}
